// ============================================================
// PERRY — THE RUN DIRECTOR
// The brain of a Solrend. Owns the beat script, Perry's walk,
// the encounter clock, and every rule a parry touches.
//
// THE OVERHAUL, 2026-07-26: encounters are no longer scripts.
// An encounter states how long the foe intends to fight; the
// director ROLLS every strike out of the foe's moveset with the
// run's seeded PRNG, escalating as the thing takes damage. The
// same husk fights you differently every Solrend. There is no
// prompt on the glass — the strike's ANGLE is performed by the
// body and answered on the guard rose (S D F high, J K L low).
//
// THE PERFORMANCE LAW THIS FILE EXISTS TO OBEY:
//   Continuous values — position, camera, timers, shake, the live
//   strike, the thrown orb — live in REFS and advance in ONE rAF
//   loop. React only hears about DISCRETE events: a beat changed,
//   a grade landed, a foe broke. Nothing here calls setState per
//   frame. That one rule is the whole difference between
//   "cinematic" and "it lags when I move."
//
//   The loop mounts ONCE (empty deps) so it is never torn down
//   mid-run. Because of that it must never close over a stale
//   callback — every function it calls is reached through
//   liveRef.current, which is refreshed on every render.
//
// Also honored: e.repeat on every key, seeded everything, the Lyf
// Cycle, the Growth Law, grant-before-flag (upstream), and NO
// mutation of the shared ACT1/ENCOUNTERS data — a second run must
// be identical to the first.
// ============================================================

import { useRef, useState, useEffect, useCallback } from "react";
import {
  ACT1, ACTS, ENCOUNTERS, ENEMIES, ATTACKS, RULES, SHOTS, QTE, GUARD, REGALIA,
  SIGHT, resolveSight, rollShuffle, themeFor,
  deriveAnimaCap, deriveMaxChao, deriveLyt, deriveGrace, deriveImperas,
  deriveTreasure, deriveFinish, deriveSight, deriveSlots, SLOT_KEYS,
  // ── 2026-07-29 ── breath, the wind clock, and the fires
  deriveLyf, deriveExhaustMs, kitupFor,
  // ── 2026-07-30 ── mastery needs the LEVEL, not the percentage
  deriveLevel,
  // ── 2026-07-30 ── the Sheathe speaks on the road, once
  sheatheSays, armAwakening,
  // ── 2026-07-30 ── THE TUTORIAL, finished. `isTeaching` is the
  // one predicate the whole teaching road hangs off; TUTORIAL is
  // every word it says.
  isTeaching, TUTORIAL,
} from "./perryData";
import {
  rnd, makeStrike, gradePress, isTimedOut, speciesMods, treasureMods,
  rollAttack, rollAngle, mirrorAngle, windScale, CHAIN_MILESTONES, pathOf,
  // ── THE PAUSE, adopted 2026-07-30 ──
  // `shiftStrike` has existed since the report card was first
  // written and has been called by NOBODY ever since — the card
  // went missing in a stale-snapshot delivery and took its only
  // caller with it. Same story for `shiftPowerClocks` below. Two
  // orphans, both correct, both waiting. They are wired now.
  shiftStrike,
} from "./qteEngine";
// ── THE REGALIA, 2026-07-28 ──────────────────────────────────
// Every power's live state, its clocks and its one gate live in
// perryPowers. This file holds NO power state of its own — see
// that file's header for why, and for the playtest it cost to
// learn. What follows is nine hooks and nothing else.
import {
  freshPowers, encounterPowers, kitPowers, speak, tickPowers,
  windMultiplier, convertGrade, spendEcho, hourMult, powerHud,
  shiftPowerClocks,
} from "./perryPowers";

const now = () => performance.now();

export default function useRunDirector({ soul, onFinish, onEvent, hintMode = "auto", actId = "act1" }) {
  // ══════════════════════════════════════════════════════════
  //  WHICH ROAD — R-07, closed 2026-07-29
  // ══════════════════════════════════════════════════════════
  // This hook imported ACT1 and hardcoded it in four places, so
  // there was no way to walk the Drowned Mile no matter what the
  // rest of the codebase had built. `ACTS`, `ACT2`, `ACT_ORDER`,
  // `unlockedActs` and `latestAct` all already existed; the fen
  // bestiary, the fen wares, `FenWorld`, `FEN_FOG` and the fen
  // branch of the light rig were all already written. Only the
  // controller had never been told.
  //
  // The knock-on was invisible and worse than the missing level:
  // `CinematicStage` reads `director.act?.biome` and was getting a
  // hardcoded "cinders" forever, which is CORRECT for Act I — so
  // nothing ever looked broken, and `FenWorld` had never once
  // rendered outside an Emperium.
  //
  // `actId` defaults to "act1", so every existing call site walks
  // exactly the road it walked before.
  const act = ACTS[actId] || ACTS.act1;
  const script = act.script || ACT1;

  // ══════════════════════════════════════════════════════════
  //  IS THIS ROAD TEACHING? — 2026-07-30
  // ══════════════════════════════════════════════════════════
  // Read ONCE, at run start, exactly like every other piece of
  // the loadout — a road cannot stop teaching halfway down itself
  // just because a milestone landed in another tab.
  //
  // It is the ONE fact three separate behaviours hang off, and
  // all three were broken in different ways before today:
  //
  //   the BANNER   promised "you cannot fall here" — and said it
  //                on every walk forever, because it read
  //                `act.teaches` with no milestone in it while
  //                the coals read the milestone. Drift.
  //   the MERCY    did not exist. `takeHit` reached zero and
  //                called `fall()`, on the teaching road same as
  //                anywhere. The banner was a lie in gold leaf.
  //   the DOOR     ESC did nothing, anywhere, ever, while the
  //                hint that advertised it rendered fine.
  //
  // One predicate in perryData now, three consumers here, and a
  // fourth in the letter floor. See `isTeaching`.
  const teaching = isTeaching(act, soul);
  const teachingRef = useRef(teaching);
  teachingRef.current = teaching;
  // ── Derived loadout — read from the Soul once, at run start ──
  const animaCap = deriveAnimaCap(soul);
  const maxChao = deriveMaxChao(soul);
  const baseLyt = deriveLyt(soul);
  // ── YOUR LYF — how many bad answers your paws forgive ──
  // Base 1. A fresh beb who whiffs once is winded, and a winded
  // beb is a beb the fen gets to hit for free.
  const lyfCap = deriveLyf(soul);
  // How long "winded" lasts. The Sharpened Answer track buys this
  // down a level at a time and can never buy it to nothing.
  const exhaustMs = deriveExhaustMs(soul);
  const grace = deriveGrace(soul);
  // ── MASTERY, 2026-07-30 ──
  // The window math needs the Read the Flame LEVEL, not just its
  // percentage, because forgiving a Treasure's weight happens in
  // thirds per level and a percentage cannot say which third you
  // are on. See `masteredWindowMod` in qteEngine.
  const graceLv = deriveLevel(soul, "grace");
  const imperas = deriveImperas(soul);
  // ── THE GRIMOIRE — the five you took, in the order you took them.
  // Read ONCE at run start, like every other piece of the loadout:
  // a Soul cannot be re-kitted halfway down the Cinders Road, and
  // a director that re-read it mid-run would be a director whose
  // key handler changes meaning under the player's fingers.
  const slots = useRef(deriveSlots(soul)).current;
  const theme = useRef(themeFor(soul?.species)).current;
  // How many coals will say their name, per encounter, from what
  // you took and what you are holding. Consumables add to it live.
  const sightPool = deriveSight(soul);
  const treasure = useRef(deriveTreasure(soul)).current;
  const finish = useRef(deriveFinish(soul)).current;
  const mods = useRef({
    grace, graceLv,
    ...speciesMods(soul?.species),
    ...treasureMods(treasure),
  }).current;

  const hasCap = !!soul?.curios?.curio_wizards_cap;
  const hasATV = !!soul?.tomes?.tome_kindled_blade;
  const hasACB = !!soul?.tomes?.tome_fork_of_embers;
  const hasPage = !!soul?.tomes?.tome_page_of_names;
  const hasHour = !!soul?.tomes?.tome_long_hour;

  // ── THE REGALIA'S LIVE STATE ──
  // A ref, like every other continuous thing in this title: clocks
  // tick in the rAF loop and charges are read inside resolve(), and
  // neither may ever cost a React render. The HUD hears about it
  // through syncHud, discretely, exactly like Chao does.
  const powers = useRef(freshPowers(soul)).current;
  // The ATV flame that bends the Regalia's clocks. Lit while the
  // Long Hour is owned and burning; a feint puts it out, same as
  // the Kindled Blade, because that is what ATV MEANS.
  const hourLit = useRef(hasHour);
  // ── THE EMPERIUM'S SKY ──
  // `CinematicStage` already reads `director.act?.biome` and already
  // renders a second world off it — that machinery was built for
  // the Drowned Mile and costs nothing to drive twice. So the
  // Emperium does not need a renderer; it needs a string. This is
  // the string.
  const biomeRef = useRef(null);
  // The last fire you sat at. The overlay reads it to draw the Kit
  // Up flourish; nothing else depends on it.
  const kitRef = useRef(null);

  // ── DISCRETE state — React may re-render on these ──────────
  const [beatIndex, setBeatIndex] = useState(0);
  const [grade, setGrade] = useState(null);
  const [line, setLine] = useState(null);
  const [over, setOver] = useState(null);            // "fell" | "arrived"
  // ── THE REPORT CARD ── { struck, spared, foe, broke, lockUntil }
  // Discrete by nature: it rises once, on an event, and the road
  // stands still while it is up. Exactly what React is for.
  const [card, setCard] = useState(null);
  // The same fact, in the shape the KEY HANDLER needs it. The
  // handler is bound once and must not re-bind every time a card
  // rises, so it reads the ref; the overlay renders the state.
  // Written synchronously by the three callbacks that move it, so
  // there is never a frame where the road is paused and the
  // handler still thinks it is not.
  const cardRef = useRef(null);
  // ── THE DOOR, ARMED ── true between the first ESC and the
  // moment SKIP_ARM_MS forgets it. Also discrete: it is a prompt,
  // it appears and disappears, and nothing reads it per frame.
  const [skipAsk, setSkipAsk] = useState(false);
  const [hud, setHud] = useState({
    anima: animaCap, chao: maxChao, lyt: baseLyt, chain: 0, bestChain: 0,
    atv: 0, foe: null, foeAnima: 0, foeMax: 0, capUsed: false, struck: 0,
    slowLeft: 0, foresight: 0, fractured: false,
    raysArmed: !!(soul?.tomes?.tome_rays_of_hope), acb: 0,
    sight: 0, kindling: 0,
    // ── THE BREATH BAR, 2026-07-29 ──
    lyf: lyfCap, lyfMax: lyfCap, winded: 0,
    // the foe's chip bar, beside its finish bar
    foeLyf: 0, foeLyfMax: 0, foeFractured: false,
    // consecutive PERFECTs — the streak that pays from 3
    streak: 0, spared: 0,
    // once-per-Kit-Up level 3 Arts, and where the next fire is
    // `baseLyt` is deriveLyt(soul), which already includes the
    // Treasure — see THE DOUBLE EDGE in `bite()`.
    kitAt: null, edge: baseLyt,
  });

  // ── CONTINUOUS state — refs only, never state ──────────────
  const pos = useRef({ x: 0, y: 0, z: 0 });
  const facing = useRef(0);                          // 0 rad = +X, down the road
  const speed = useRef(0);                           // 0 idle · 1 walking
  const shake = useRef(0);
  const foePos = useRef([{ x: 6, y: 0, z: 0 }, { x: 6, y: 0, z: 1.3 }]);
  const foeState = useRef([
    { phase: "hidden", t: 0, angle: null, atk: null, prog: 0, lane: 1, path: "step", actor: true },
    { phase: "hidden", t: 0, angle: null, atk: null, prog: 0, lane: -1, path: "step", actor: false },
  ]);
  const foeId = useRef("burning_husk");
  const shotRef = useRef(SHOTS.s_wide_open);
  const shotKey = useRef("s_wide_open");
  const fpRef = useRef(false);

  // ── THE FIGHT'S CONTINUOUS PIECES — all refs, read by the stage
  const strikeRef = useRef(null);      // the live strike (telegraph source)
  const guardRef = useRef({ key: null, at: 0, grade: null });  // Treasure pose
  const orbRef = useRef({ active: false, t0: 0, t1: 0, from: null, to: null, tell: "#ffb058" });
  const sparkRef = useRef({ at: 0, angle: "D", grade: "GOOD", tell: "#ffb058" });
  const fractureRef = useRef(0);       // performance.now() until which the foe is open

  // ── THE COALS — what the training rose is allowed to say ──
  // A REF, not state, for the same reason everything continuous in
  // this title is a ref: it is read inside useFrame by six sprites
  // and inside a self-polling rAF by the corner rose, and a parry
  // must never cost a React render.
  //   tier     "lettered" | "coloured" | "shuffled" | "dark"
  //   shuffle  key → the angle it HANGS at, or null for the truth
  //   left     charges remaining this encounter (for the HUD only)
  const sightRef = useRef({ tier: SIGHT.LETTERED, shuffle: null, left: 0 });

  // One place the tier is computed, so the world rose, the corner
  // rose and the HUD can never disagree about what you can see.
  //
  // IT COMPUTES THE **BASE** TIER ONLY — what you can see with no
  // charge burning. The live charge is deliberately NOT baked in
  // here, because that would make this a piece of sticky state
  // that has to be un-set at the end of every strike, on every one
  // of five exit paths, forever. Instead both consumers overlay
  // `strikeRef.current.named` themselves, which is a fact that
  // expires on its own when the strike does. Self-healing beats
  // correct-if-you-remember, and this file has already lost one
  // playtest to a latch that never fired.
  const syncSight = useCallback(() => {
    const s = sightRef.current;
    s.tier = resolveSight({ soul, hintMode, enc: encRef.current?.enc || null, named: false });
    s.left = vitals.current.sight || 0;
    // A shuffle only means anything while the coals are shuffled.
    if (s.tier !== SIGHT.SHUFFLED) s.shuffle = null;
    else if (!s.shuffle) s.shuffle = encRef.current?.shuffle || null;
  }, [soul, hintMode]);

  // ── THE FOE'S PIPS — caP'n, 2026-07-27 ──
  // "he has a mini health bar we dim every time we parry an attack"
  // A ref, not state, because it is read inside useFrame by a thing
  // floating over the warden's head and must never cost a render.
  //   anima/max  the pips themselves
  //   hitAt      the moment a pip was actually taken (a BITE)
  //   ringAt     the moment ANY answer landed on it — the bar
  //              shivers and the struck pip flares even when the
  //              Lyf Cycle says the number does not move. Honest:
  //              it shows contact, it does not fake damage.
  //   show       whether this foe carries a bar at all (ENEMIES.bar)
  const foeVitals = useRef({ anima: 0, max: 0, hitAt: 0, ringAt: 0, grade: null, show: false });

  // ── Run bookkeeping ────────────────────────────────────────
  const R = useRef(rnd(Date.now() % 100000)).current;
  const beat = useRef(0);
  const beatT = useRef(0);
  const flags = useRef({});          // per-RUN beat flags (never on shared data)
  const holdUntil = useRef(0);
  // ── THE PAUSE ─────────────────────────────────────────────
  // `paused` stops the loop dead; `pausedAt` remembers when, so
  // every absolute deadline in the title can be moved forward by
  // exactly the time we stood still. Every clock in this game is
  // an absolute performance.now() stamp, which is the right shape
  // for a game that never stops and precisely the wrong shape for
  // one that does — see `resumeFromCard`.
  const paused = useRef(false);
  const pausedAt = useRef(0);
  // A card owed but not yet raised. The fight ends, the break
  // gets its 1.5 seconds to read, and THEN the card comes up —
  // covering the kill with a scoreboard would throw away the one
  // frame the whole fight was for.
  const cardPending = useRef(null);
  // The door out, half-open. Holds the deadline of the first ESC.
  const skipArm = useRef(0);
  const skipTimer = useRef(0);
  const raf = useRef(0);
  const last = useRef(0);
  const running = useRef(true);
  const encRef = useRef(null);
  const stats = useRef({
    parries: 0, perfects: 0, ruins: 0, bestChain: 0, chain: 0,
    // ── STRUCK ── how many blows have actually landed on you.
    // The tutorial banner's tally. It exists because "you cannot
    // fall here" is only reassuring if the road can still visibly
    // HURT you — a teaching road that never marks you teaches
    // that nothing matters. Counts on the teaching act; harmless
    // everywhere else, because only the banner reads it.
    struck: 0,
    // ── SPARED ── how many times your Anima actually reached
    // zero on a road that was not allowed to keep you. THE number
    // the report card exists to print, and the entire reason the
    // deathlessness is a lesson rather than a cheat: "you would
    // have fallen twice" is a sentence a player carries onto the
    // next road. `struck` and `spared` are deliberately separate
    // facts — hit nine times and dead twice is a completely
    // different beb from hit twice and dead twice.
    spared: 0,
    // The same two, for the fight that just ended. Zeroed by
    // startEncounter, read by the card, and never anywhere else.
    struckEnc: 0, sparedEnc: 0,
    // Set only by the ESC door. App reads it to know that an
    // arrival was not walked — see THE REFUSALS.
    skipped: false,
    // consecutive PERFECTs. Distinct from `chain` on purpose — a
    // chain survives a GOOD, a streak does not. Precision and
    // survival are different virtues and pay separately.
    streak: 0, bestStreak: 0, exhausted: 0,
    seen: [], fell: false, arrived: false, chaoSpentThisEnc: 0, grantedChains: {},
    // ── THE LEG — the stretch of road between two fires ──
    // The Growth Law's unit of measurement now. `legsWalked` starts
    // at 0 so the very first Kit Up (which you reach having walked
    // no leg at all) cannot hand out a free ceiling.
    chaoSpentThisLeg: 0, legsWalked: 0, kitups: 0,
    calamities: 0,
    // ⋔ EARNED ON THE ROAD, 2026-07-27. Counted here so the end
    // card can show it and App can settle it in ONE transaction.
    // Never written per beat — see RULES.KINDLING_GOOD.
    kindling: 0,
    // ══════════════════════════════════════════════════════════
    //  THE REFUSALS — R-54, caP'n's restriction achievements
    // ══════════════════════════════════════════════════════════
    // Five of the six new achievements are earned by NOT doing
    // things, so each is a boolean that starts true and only ever
    // goes one way. There is no path back to true inside a run and
    // there must never be one.
    //
    // THEY ARE CHECKED AT THE SPEND, NEVER AT THE FINISH. A player
    // who bought a Treasure and never equipped it must still fail
    // `perry:no_treasures` — "I didn't take any items" means the
    // shop, not the loadout. Same for an Art: SPEAKING one breaks
    // it, owning one does not, because owning is a fact about your
    // sword and speaking is a decision you made in a fight.
    //
    // `untouched` is the exception that proves it: it cannot be
    // checked at a spend because nothing is spent, so it lives in
    // `takeHit` — the one line every blow that ever reaches Perry
    // has to pass through.
    pureNoArts: true,      // never spoke an Art
    pureUntouched: true,   // never lost a point of Anima
    // Ruins scored while the held weapon is mid-Awakening.
    awakenRuins: 0,
  });
  const vitals = useRef({
    anima: animaCap, chao: maxChao, lyt: baseLyt, atv: 0,
    // ── BREATH ── refills at a Kit Up and at the end of a wind.
    lyf: lyfCap, windedUntil: 0,
    // ── LEVEL 3 ARTS ── one apiece, per Kit Up, not per fight.
    // A Set of regalia ids spoken since the last fire. Cleared by
    // kitUp() and by nothing else — deliberately NOT by
    // startEncounter, which is the whole change.
    spentL3: new Set(),
    capUsed: false, slowLeft: 0, foresight: 0,
    raysArmed: !!(soul?.tomes?.tome_rays_of_hope),
    // charges left that will make a coal say its name
    sight: 0,
    pageLeft: hasPage ? RULES.SIGHT_PAGE : 0,   // the Tome, once per Solrend
  });

  // ── Sync refs → React. Discrete events only. ───────────────
  const syncHud = useCallback(() => {
    const v = vitals.current;
    const e = encRef.current;
    setHud({
      anima: v.anima, chao: v.chao, lyt: v.lyt + v.atv, atv: v.atv,
      chain: stats.current.chain, bestChain: stats.current.bestChain,
      foe: e?.foe || null, foeAnima: e?.foeAnima ?? 0, foeMax: e?.foe?.anima ?? 0,
      capUsed: v.capUsed, slowLeft: v.slowLeft, foresight: v.foresight,
      fractured: now() < fractureRef.current,
      raysArmed: v.raysArmed, acb: e?.acb || 0,
      sight: v.sight, kindling: stats.current.kindling,
      lyf: v.lyf, lyfMax: lyfCap,
      winded: Math.max(0, v.windedUntil - now()),
      foeLyf: Math.max(0, e?.foeLyf ?? 0), foeLyfMax: e?.foe?.lyf ?? 0,
      foeFractured: !!e && (e.foeLyf ?? 0) <= 0,
      streak: stats.current.streak, struck: stats.current.struck,
      // ── SPARED, 2026-07-30 ── the running count of falls the
      // teaching road took for you. Lives on the HUD for exactly
      // the reason `struck` does: it only moves on a discrete
      // event, so React is the right thing to hear about it.
      spared: stats.current.spared,
      // Same fix as `bite()` — deriveLyt already carries the
      // Treasure. The HUD was printing a number the engine never
      // used to deal, which is the worst kind of wrong: plausible.
      edge: v.lyt + v.atv,
      // ── THE REGALIA ──
      // A flat snapshot, taken here and never held as a reference,
      // so the overlay can never read a charge one frame late.
      // Clocks arrive as milliseconds-remaining and are re-derived
      // every sync — the overlay animates them itself, on its own
      // rAF, and never re-renders for them.
      pw: powerHud(powers, now()),
      hourLit: hourLit.current,
    });
  }, []);

  // The Sheathe's beat queue. Declared above `say` because `say`
  // reads it — see the guard immediately below.
  const speech = useRef({ queue: [], nextAt: 0 });
  // Belt AND braces: the milestone stops it across runs, this stops
  // it twice inside one run before the write has landed.
  const revealed = useRef(false);

  // ── THE ROAD DOES NOT TALK OVER THE SHEATHE ──────────────
  // `who` is null for the narrator this game has always had, and
  // set for anybody actually speaking. While a beat queue is
  // draining, an unattributed line is DROPPED rather than shown —
  // otherwise a waypoint's flavour text stomps the reveal
  // mid-sentence and the biggest scene in the title reads as
  // flicker. Somebody talking always outranks the weather.
  //
  // It self-heals: the queue empties and the road gets its voice
  // back with no flag to reset and nothing to leak.
  const say = useCallback((text, who = null) => {
    if (!who && speech.current.queue.length) return;
    setLine(text ? { text, at: now(), who } : null);
  }, []);

  // ══════════════════════════════════════════════════════════
  //  THE SHEATHE ON THE ROAD — caP'n's opening beat, 2026-07-30
  // ══════════════════════════════════════════════════════════
  // "After Perry's first Parry, the Sheathe (who Perry thinks at
  //  first is the sword not Sheathe) was monologuing for us the
  //  story and then reveals himself to Perry (the sword chimes in
  //  too... yeah it can talk.)"
  //
  // A QUEUE, not a modal. The fight does not stop for it — the
  // Sheathe talks over the top of the road and the road keeps
  // swinging, which is the only way this reads as a thing that is
  // happening TO Perry rather than a cutscene he was put inside.
  //
  // Drained in the rAF loop off `nextAt`, exactly like a strike.
  // No timers, no state, nothing that survives an unmount — the
  // Determinism Law's neighbour: anything that ticks lives on the
  // one clock this file already owns.
  const tellBeats = useCallback((beats, gap = 2600) => {
    if (!beats?.length) return;
    speech.current.queue = [...beats];
    speech.current.gap = gap;
    speech.current.nextAt = now() + 400;
  }, []);

  const tickSpeech = useCallback((t) => {
    const sp = speech.current;
    if (!sp.queue.length || t < sp.nextAt) return;
    const beat = sp.queue.shift();
    // `world` beats are stage direction and get a shorter hold —
    // nobody needs three seconds to read leaves rustling.
    const hold = beat.who === "world" ? 1800 : (sp.gap || 2600);
    sp.nextAt = t + hold;
    say(beat.text, beat.who);
  }, [say]);

  // ── THE WIND ENDING — checked in the loop, once, cheaply ──
  // A wind is over when the clock says so, and when it is over the
  // breath comes ALL the way back. Deliberately not a trickle:
  // partial stamina regen turns every fight into arithmetic, and
  // this title has never once asked anybody to do arithmetic
  // mid-beat.
  const tickWind = useCallback((t) => {
    const v = vitals.current;
    if (!v.windedUntil || t < v.windedUntil) return;
    v.windedUntil = 0;
    v.lyf = lyfCap;
    say("Your paws come back to you.");
    syncHud();
  }, [lyfCap, say, syncHud]);

  // ── THE PURSE — caP'n, 2026-07-27 ──────────────────────────
  // "every single correct parry, perfect awards more and so on,
  //  awards currency... even if you can't make it to the village
  //  you can still eventually level up and try to progress."
  //
  // Deliberately NOT a `fire()`. Every other reward in this file
  // hands the write up to App immediately; ⋔ does not, because a
  // runTransaction per parry would hammer Firebase and stutter the
  // loop. It banks in stats and settles once, at the end, whether
  // you arrived or fell. The road pays even when it beats you.
  const purse = useCallback((amount) => {
    if (amount > 0) stats.current.kindling += amount;
  }, []);
  const fire = useCallback((type, payload) => { if (onEvent) onEvent(type, payload); }, [onEvent]);

  const cutTo = useCallback((key) => {
    const s = SHOTS[key];
    if (!s) return;
    shotKey.current = key;
    shotRef.current = s;
    fpRef.current = s.mode === "fp";
  }, []);

  // Set every living foe's phase at once. `actor` is the one
  // actually swinging; the others hold their idle.
  const setFoePhase = useCallback((phase, extra = {}, onlyActor = null) => {
    for (let i = 0; i < 2; i++) {
      const st = foeState.current[i];
      if (st.phase === "hidden") continue;
      if (onlyActor != null && i !== onlyActor) {
        if (phase === "recover" || phase === "break" || phase === "retreat") {
          foeState.current[i] = { ...st, phase, t: 0, prog: 0 };
        }
        continue;
      }
      foeState.current[i] = { ...st, phase, t: 0, prog: 0, angle: null, atk: null, ...extra };
    }
  }, []);

  // ── The fall — the only fail state ─────────────────────────
  const fall = useCallback(() => {
    running.current = false;
    stats.current.fell = true;
    strikeRef.current = null;
    orbRef.current.active = false;
    speed.current = 0;
    setOver("fell");
  }, []);

  // ══════════════════════════════════════════════════════════
  //  THE REPORT CARD — raise, and resume
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-07-30: "we had a pop up after every fight telling
  //  you how many times you could've died given your Anima."
  //
  // This is the half of the Tutorial that makes the other half
  // honest. Being unkillable is not a lesson; being unkillable
  // and then shown the bill is. The card prints two numbers and
  // no advice — the numbers are the advice.
  const raiseCard = useCallback(() => {
    const p = cardPending.current;
    if (!p) return;
    cardPending.current = null;
    paused.current = true;
    pausedAt.current = now();
    speed.current = 0;
    const c = { ...p, lockUntil: now() + RULES.CARD_LOCK_MS };
    cardRef.current = c;
    setCard(c);
  }, []);

  // ── COMING BACK — every deadline moves forward by `d` ──────
  // THE WHOLE LIST IS HERE AND THAT IS THE POINT, exactly like
  // `shiftStrike`'s: this game keeps time in absolute stamps, and
  // a stamp that does not move through a pause is a clock that
  // quietly ran while the player was reading. A King's Grace
  // spoken on the way into a fight would burn its whole hour
  // behind a scoreboard.
  //
  // The two hard ones are not here because they are not times:
  // counts and flags were never clocks and must not be touched.
  //
  // `strikeRef` and `encRef` are null every time this runs today
  // — the card only rises between fights. They are shifted anyway
  // and deliberately: the day a card is raised mid-encounter (a
  // Calamity survived, say) this function is already correct, and
  // both helpers are null-safe by design. Exception Law — build
  // the seam, it costs two lines.
  const resumeFromCard = useCallback(() => {
    const d = now() - pausedAt.current;
    paused.current = false;
    pausedAt.current = 0;
    if (d > 0) {
      const v = vitals.current;
      holdUntil.current += d;
      if (v.windedUntil) v.windedUntil += d;
      if (fractureRef.current) fractureRef.current += d;
      if (guardRef.current.at) guardRef.current.at += d;
      if (foeVitals.current.hideAt) foeVitals.current.hideAt += d;
      if (speech.current.nextAt) speech.current.nextAt += d;
      const e = encRef.current;
      if (e && e.nextAt) e.nextAt += d;
      shiftPowerClocks(powers, d);
      shiftStrike(strikeRef.current, d);
    }
    last.current = now();
    cardRef.current = null;
    setCard(null);
  }, [powers]);

  // ══════════════════════════════════════════════════════════
  //  THE DOOR OUT — ESC, twice
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-07-30: "You can skip it altogether and go to the
  //  kit-up to get your proper rewards."
  //
  // So a skip is an ARRIVAL, not a forfeit. It ends the run the
  // same way the far end of the road does — the milestone lands,
  // Hollowmere opens, the arrival grant pays. What it does not do
  // is pay KINDLING_TUTORIAL, which is banked on the arrive beat
  // this never reaches. Walking beats skipping by exactly the
  // amount walking is worth, and by nothing else.
  //
  // TWO PRESSES. ESC closes every other screen in this title with
  // one tap, and one stray tap must never be able to end the
  // road. The arm forgets itself after SKIP_ARM_MS.
  const skipRoad = useCallback(() => {
    clearTimeout(skipTimer.current);
    setSkipAsk(false);
    skipArm.current = 0;
    paused.current = false;
    cardPending.current = null;
    cardRef.current = null;
    setCard(null);
    running.current = false;
    strikeRef.current = null;
    orbRef.current.active = false;
    speed.current = 0;
    stats.current.skipped = true;
    stats.current.arrived = true;
    say(TUTORIAL.skipLine);
    setOver("arrived");
  }, [say]);

  const armSkip = useCallback(() => {
    skipArm.current = now() + RULES.SKIP_ARM_MS;
    setSkipAsk(true);
    clearTimeout(skipTimer.current);
    skipTimer.current = setTimeout(() => {
      skipArm.current = 0;
      setSkipAsk(false);
    }, RULES.SKIP_ARM_MS);
  }, []);

  // The one timer this file owns, and it does not get to outlive
  // the run. (Everything else ticks on the rAF loop, which the
  // loop's own cleanup cancels.)
  useEffect(() => () => clearTimeout(skipTimer.current), []);

  // ── Chain milestones — App grants, then flags ──────────────
  const bumpChain = useCallback(() => {
    const s = stats.current;
    s.chain += 1;
    if (s.chain > s.bestChain) s.bestChain = s.chain;
    for (const m of CHAIN_MILESTONES) {
      if (s.chain === m && !s.grantedChains[m]) {
        s.grantedChains[m] = true;
        fire("chain", m);
      }
    }
  }, [fire]);

  // ── ENCOUNTER START ────────────────────────────────────────
  const startEncounter = useCallback((encId) => {
    const enc = ENCOUNTERS[encId];
    if (!enc) return;
    const foe = ENEMIES[enc.enemy];
    const v = vitals.current;

    // ── CHAO NO LONGER REFILLS HERE (caP'n, 2026-07-29) ──
    // It refills at a KIT UP — a village, a bonfire, a chapel, the
    // Lady, a stranger's fire, a standing stone. Between two fires
    // your breath is a tank you are carrying, and "do I spend one
    // on this husk or save it for whatever is past the bend" became
    // a real question with a real wrong answer. See kitUp().
    v.atv = 0;
    v.slowLeft = 0;
    v.foresight = 0;
    stats.current.chaoSpentThisEnc = 0;
    // The report card's two numbers are per-FIGHT. The run totals
    // beside them keep counting; these do not.
    stats.current.struckEnc = 0;
    stats.current.sparedEnc = 0;
    // ── THE REGALIA NO LONGER RESETS WITH THE FOE, 2026-07-29 ──
    // This now renews the per-FOE allowances only (the Hollow
    // Crown's free power, the Hearth's once-a-foe). Armed charges
    // — Shadow, Echo, Mirage, The Answer, the King's hour, the
    // Emperium's domain — SURVIVE, and are cleared at the next
    // fire by kitPowers() instead.
    //
    // The stockpiling worry that justified the old behaviour is
    // handled by the currency rather than by the reset: you cannot
    // bank three Mirages across three husks because you do not have
    // nine Chao, and you will not get more until you find a fire.
    encounterPowers(powers, soul);
    hourLit.current = hasHour;
    // ── THE COALS' CHARGES ──
    // The standing pool (node + Treasure) refills per encounter,
    // exactly like Chao does. The Tome's three do NOT — a Page of
    // Names is torn out once and spends across the whole Solrend,
    // which is what makes deciding WHEN to be seen a real choice.
    v.sight = sightPool;
    if (v.pageLeft > 0) {
      v.sight += v.pageLeft;
      v.pageLeft = 0;
      fire("consumeTome", "tome_page_of_names");   // torn out once
      say("A page you have never been able to read gets warm in your paw, and then does not.");
    }
    if (!stats.current.seen.includes(foe.id)) stats.current.seen.push(foe.id);
    foeId.current = foe.id;
    fractureRef.current = 0;

    // The moveset is COPIED, never the shared object — the aggro
    // ladder reads it every roll and must not be able to write it.
    const moveset = { ...(enc.moveset || foe.moveset || { swipe: 1 }) };

    let beats = enc.beats || 3;
    let acb = 0;
    if (hasACB) {
      // ── ACB — the Fork of Embers. The FOE names the number. ──
      acb = 1 + Math.floor(R() * 3);
      v.chao += acb;                       // a gift may exceed your Max
      beats += acb;
      say(`The Husk raises ${acb} charred finger${acb > 1 ? "s" : ""}. The fire takes the wager.`);
    } else if (enc.intro) {
      say(enc.intro);
    }

    const count = enc.count || 1;
    for (let i = 0; i < 2; i++) {
      foeState.current[i] = {
        phase: i < count ? "rise" : "hidden", t: 0, angle: null, atk: null, prog: 0,
        lane: i === 0 ? 1 : -1, path: "step", actor: i === 0,
      };
      // a rough offstage mark; LaneDriver takes over on frame one
      foePos.current[i] = { x: pos.current.x + 2.4 + i * 0.6, y: 0, z: -0.4 + i * 1.0 };
    }

    foeVitals.current = {
      anima: foe.anima, max: foe.anima, hitAt: 0, ringAt: 0,
      grade: null, show: !!foe.bar, hideAt: 0,
    };

    encRef.current = {
      enc, foe, acb, count, moveset,
      beatsLeft: beats,
      foeAnima: foe.anima, foeLyf: foe.lyf || 0, broken: false,
      aggro: 0, lastAtk: null, repeats: 0, lastAngle: null,
      actor: 0, calQueue: null, echo: null,
      ladleLeft: treasure.ladle || 0,
      // Rolled off the RUN SEED, not Math.random, so the same
      // Solrend moves the coals the same way twice. Deranged by
      // construction — no coal may sit at its own angle, or the
      // shuffle would sometimes do nothing and the player would
      // learn to distrust a tell that was telling the truth.
      shuffle: (enc.sight === SIGHT.SHUFFLED) ? rollShuffle(R) : null,
      nextAt: now() + 950,
    };
    speed.current = 0;
    sightRef.current.shuffle = null;   // re-resolve against the new encounter
    syncSight();
    syncHud();
  }, [maxChao, hasACB, hasHour, R, soul, fire, say, syncHud, syncSight, sightPool, treasure]);

  // ── ENCOUNTER END ──────────────────────────────────────────
  const endEncounter = useCallback((broke) => {
    const e = encRef.current;
    if (!e) return;
    const v = vitals.current;

    if (broke) {
      stats.current.ruins += 1;
      // ── THE AWAKENING COUNTS ITS DEAD, 2026-07-30 ──
      // A Ruin only counts toward an Awakening if the weapon
      // walking the mission is the one in your paws. Banked here
      // and settled in ONE transaction at the end of the run, like
      // every other counter in this file — a database write per
      // dead husk is the stutter the Annihilation scar taught us
      // about, and a mission is not more urgent than a purse.
      if (armAwakening(soul, treasure.id)) stats.current.awakenRuins += 1;
      // ── ONE COIN NOW (Two-Currency Law, 2026-07-29) ──
      // The `fire("renown", …)` that used to sit beside every purse
      // call is gone from this file entirely. Renown and Kindling
      // were always the same act measured twice; they are one act
      // measured once now, and `purse` is the only measurer.
      purse(RULES.KINDLING_RUIN);
      if (soul?.species === "antdroid") purse(1);   // Machine/Time perk
      // ── THE LEDGER HEARS ABOUT THE KILL, 2026-07-30 ──
      // The foe's id and nothing else. App decides whether any
      // achievement is owed — this file has never known what an
      // achievement is and is not starting now. One event, every
      // Ruin, so the next boss worth a ledger entry costs a line
      // in App and nothing here. (Exception Law: an event, not a
      // list of the four enemies that mattered on the day.)
      fire("ruin", foeId.current);
      // THE GROWTH LAW MOVED TO kitUp(). Restraint is now measured
      // across a whole leg of road, not a single husk.
    }
    setFoePhase(broke ? "break" : "retreat");
    // the bar does NOT vanish on the kill — you get to watch the
    // last pip go out. `hideAt` retires it a beat later so it can
    // never outlive the fight and float over an empty road.
    foeVitals.current = {
      ...foeVitals.current,
      anima: broke ? 0 : foeVitals.current.anima,
      hitAt: broke ? now() : foeVitals.current.hitAt,
      hideAt: now() + 1400,
    };
    v.atv = 0;                    // the flame does not travel between foes
    v.foresight = 0;
    v.sight = 0;                  // and neither do the charges
    encRef.current = null;
    sightRef.current.shuffle = null;
    strikeRef.current = null;
    orbRef.current.active = false;
    fractureRef.current = 0;
    holdUntil.current = now() + 1500;   // let the break read before the cut
    // ── THE CARD IS OWED, NOT RAISED ──
    // Queued here, raised by the loop once the 1.5s above has
    // run out. Raising it now would drop a scoreboard over the
    // exact frame the fight was for. It rises on BOTH exits —
    // a husk that shambled off still hit you, and how many times
    // it nearly finished you is the same lesson either way.
    if (teachingRef.current) {
      cardPending.current = {
        struck: stats.current.struckEnc,
        spared: stats.current.sparedEnc,
        totalSpared: stats.current.spared,
        foe: e.foe?.name || null,
        broke,
      };
    }
    syncHud();
  }, [soul, fire, purse, say, syncHud, setFoePhase]);

  // ══════════════════════════════════════════════════════════
  //  KIT UP — the only place breath comes back
  // ══════════════════════════════════════════════════════════
  // caP'n, 2026-07-29: "we're changing that to per run until your
  //  next checkpoint — aka another village, or bonfire, or church,
  //  lady, stranger, statue. So many checkpoints where we can stop
  //  and Kit Up. Kit Up spots refresh your abilities and Chao —
  //  the big ones are only one use per refresh: Emperium, U'hlti,
  //  any level 3 Art."
  //
  // Three things happen here and nowhere else:
  //   1. Chao → Max
  //   2. Lyf → Max (your paws stop shaking)
  //   3. every level-3 Art becomes speakable again
  //
  // THE GROWTH LAW MOVED HERE TOO, and it got much harder on
  // purpose. It used to ask "did you clear this ONE foe without
  // spending?" — trivially yes, since breath was free every fight.
  // It now asks "did you get from that fire to THIS fire without
  // spending a breath?", which across a whole leg of road is a
  // genuine act of restraint and is meant to be rare.
  const kitUp = useCallback((kind = "bonfire") => {
    const v = vitals.current;
    const s = stats.current;
    const spot = kitupFor(kind);

    if (s.chaoSpentThisLeg === 0 && !soul?.deviant && s.legsWalked > 0) {
      fire("growth");
      say("You came the whole way here without spending a breath. Something in you widens, and does not narrow again.");
    } else {
      say(spot.line);
    }

    // ══════════════════════════════════════════════════════════
    //  A FIRE GIVES EVERYTHING BACK — caP'n, 2026-07-30
    // ══════════════════════════════════════════════════════════
    // "reaching a kit-up spot is a guaranteed refresh on everything
    //  you have <3 its a save/checkpoint."
    //
    // Anima used to be the one bar a fire did not touch, which made
    // a Solrend a one-way slide from full to nothing regardless of
    // what you were carrying. More max Anima only lengthened the
    // slide; it never changed how a fight FELT. Now a fire is a
    // genuine checkpoint and the Kindled Heart track compounds —
    // a bigger ceiling is a bigger refill, every fire, all road.
    //
    // It also makes caP'n's own placement rule load-bearing: fires
    // go BEFORE hard things, not after. The stretch from the last
    // fire to the boss is the test, and it is a test you enter
    // whole. (A minimap and chosen paths are roadmapped on top of
    // this — R-53.)
    v.anima = animaCap;
    v.chao = maxChao;
    v.lyf = lyfCap;
    v.windedUntil = 0;
    v.spentL3.clear();
    v.atv = 0;
    // Everything you were still holding goes out with the embers.
    // Charges survive FIGHTS now; they do not survive FIRES.
    kitPowers(powers, soul);
    biomeRef.current = null;
    s.chaoSpentThisLeg = 0;
    s.legsWalked += 1;
    s.kitups += 1;
    kitRef.current = { kind, at: now() };
    fire("kitup", kind);
    syncHud();
  }, [animaCap, maxChao, lyfCap, soul, fire, say, syncHud]);

  // ── Queue the next strike after this one resolves ──────────
  // A Calamity's next beat and a Slash's second beat are part of
  // the SAME motion — they must land a fixed distance after the
  // beat before them, not a fixed distance after your press.
  // Otherwise answering early would speed the combo up, which is
  // exactly backwards.
  const scheduleNext = useCallback((t, strike = null) => {
    const e = encRef.current;
    if (!e) return;
    if (e.echo || (e.calQueue && e.calQueue.length)) {
      e.nextAt = strike?.impactAt ?? t;
      return;
    }
    const gap = e.enc.gap || QTE.BEAT_GAP;
    e.nextAt = t + gap[0] + R() * (gap[1] - gap[0]);
  }, [R]);

  // ══════════════════════════════════════════════════════════
  //  THE BITE — chip the skin, then break the thing
  // ══════════════════════════════════════════════════════════
  // REWRITTEN 2026-07-29. caP'n reversed the Lyf Cycle for this
  // title, deliberately and with his eyes open: "any parry chips
  // the enemy." So the old rule — a bite that does not EXCEED Lyf
  // accomplishes nothing at all — is gone from Perry, and the
  // Build Guide says why. (Blueprint Law 3 permits exactly this:
  // port the Cycle, or consciously choose not to and write it down.)
  //
  // TWO BARS, IN ORDER, ALWAYS THE SAME ORDER:
  //
  //   LYF — the wall. Comes off by (your Lyt + your weapon's edge
  //   + any lit ATV), plus whatever your Treasure pierces. It does
  //   NOT heal between beats. Grind it to zero and the thing is
  //   FRACTURED for the rest of the fight.
  //
  //   ANIMA — the finish. One landed answer, one Anima, FLAT. Your
  //   axe does not matter here and that is the point: a big edge
  //   gets you through the wall faster, it does not shorten the
  //   execution. This is what keeps the Anima ceiling of 5 meaning
  //   something instead of evaporating under a good weapon.
  //
  // A foe with `lyf: 0` walks on pre-Fractured and every parry
  // takes an Anima. Same code path, no special case, no branch —
  // the burning husk in the prologue and T'yphiz at the end of the
  // road are the same three lines of arithmetic.
  const bite = useCallback(() => {
    const e = encRef.current;
    const v = vitals.current;
    if (!e) return false;
    // ══════════════════════════════════════════════════════════
    //  THE DOUBLE EDGE — fixed 2026-07-29, ceilings pass
    // ══════════════════════════════════════════════════════════
    // This line read `v.lyt + v.atv + (treasure.lyt || 0)` and had
    // since the two-bar model landed. `v.lyt` is `deriveLyt(soul)`,
    // and deriveLyt ALREADY adds `deriveTreasure(soul).lyt` — so
    // every weapon in the game was counting TWICE against a foe's
    // skin. A fresh beb chipped 3 a swing, not 2; the Clapper
    // chipped 11, not 6.
    //
    // It is caP'n's own law arriving as a bug: two hand-maintained
    // copies of the same arithmetic will drift. `deriveLyt` is the
    // single source and it is complete. Nothing downstream adds to
    // it again. The prose everywhere ("your Lyt PLUS your weapon's
    // edge") describes what deriveLyt already did internally, and
    // was read here as an instruction to do it once more.
    const edge = v.lyt + v.atv;

    // ── STILL WEARING SKIN ──
    if ((e.foeLyf || 0) > 0) {
      e.foeLyf -= edge + (treasure.pierce || 0);
      foeVitals.current.lyf = Math.max(0, e.foeLyf);
      foeVitals.current.hitAt = now();
      shake.current = Math.max(shake.current, 0.42);
      if (e.foeLyf <= 0) {
        e.foeLyf = 0;
        fractureRef.current = now() + RULES.FRACTURE_MS;
        // THE BREACH — recurring, and it says so. See the header.
        say("Its skin gives out. For a moment there is nothing between your answer and the thing itself.");
        purse(RULES.KINDLING_BREACH);
        setFoePhase("fracture", {}, e?.actor ?? 0);
      } else {
        say(`You take a piece out of it. ${e.foeLyf} left of whatever it was wearing.`);
      }
      syncHud();
      return false;
    }

    // ── BREACHED — the finish, one Anima per answer, flat ──
    e.foeAnima -= 1;
    e.aggro = Math.max(0, (e.foe.anima || 1) - Math.max(0, e.foeAnima));
    foeVitals.current.anima = Math.max(0, e.foeAnima);
    foeVitals.current.hitAt = now();
    shake.current = Math.max(shake.current, 0.75);
    if (e.foeAnima <= 0) {
      e.broken = true;
      say("Clean through. It comes apart like a log kicked open.");
      endEncounter(true);
      return true;
    }

    // ══════════════════════════════════════════════════════════
    //  THE WALL COMES BACK — caP'n, 2026-07-30
    // ══════════════════════════════════════════════════════════
    // "you parry 3 times (Anima exposed), you attack 1 more (Anima
    //  lowered and their Lyf regenerated), you attack 3 more expose
    //  the Anima, 1 more to ruin it — never the 0 fracture rule
    //  like Terror, if that was a thing let's get rid of it."
    //
    // It was a thing. `"It is open now, and it stays open"` was the
    // Terror-ism, ported on 2026-07-29 and wrong for this title.
    // Lyf is not a one-time entry fee. It is the skin the thing
    // grows back around every piece of itself you take.
    //
    // WHY THIS IS THE WHOLE FIX FOR "MY ITEMS DON'T MATTER."
    // Under break-once you paid the wall tax ONCE PER FIGHT, so a
    // huge edge shaved a few opening answers and then stopped
    // mattering — half the bestiary walks on at `lyf: 0` and a
    // Clapper was mathematically identical to the starter branch
    // against every one of them. Under regeneration you pay the
    // wall tax ONCE PER ANIMA, so your edge multiplies against the
    // foe's entire health bar:
    //
    //   answers = anima × (⌈lyf ÷ edge⌉ + 1)
    //
    //   T'yphiz (Lyf 5 · Anima 3) · edge 1 → 18 answers
    //                              edge 2 → 12
    //                              edge 3 →  9
    //
    // Three times the fight for an under-geared beb. THAT is an
    // endgame weapon, and no enemy timing number moved to get it.
    //
    // NO OVERFLOW, and caP'n's own worked example is what settles
    // it: the answer that strips the wall strips the wall, and the
    // NEXT answer takes the Anima. Nothing spills between the two.
    // One answer, one piece — the Small Number Law, and the reason
    // the Anima ceiling still means something under a big edge.
    e.foeLyf = e.foe.lyf || 0;
    foeVitals.current.lyf = e.foeLyf;
    if (e.foeLyf > 0) {
      fractureRef.current = 0;       // the opening closes with the skin
      say("You take a piece of the thing itself — and it closes over the wound. Again, then.");
    }
    return false;
  }, [say, purse, endEncounter, treasure, setFoePhase, syncHud]);

  // ── THE FREE HIT — straight past the skin, into the thing ──
  // Echo and Mirage owe the foe a hit that ignores Lyf entirely.
  // Under the old Lyf Cycle these were ESCAPE DOORS — the only way
  // through a wall you could not out-bite. Chip damage arrived on
  // 2026-07-29 and the wall stopped being absolute, so they are not
  // doors any more; they are simply the strongest thing you can do,
  // because they skip the grind and land on Anima directly. Same
  // code, better deal, no rebalance needed.
  //
  // It routes through the same foeAnima / aggro / pip bookkeeping
  // as bite() rather than duplicating it, because two functions
  // that both reduce a foe's Anima are two functions that will
  // eventually disagree about when it died.
  const freeBite = useCallback((n = 1) => {
    const e = encRef.current;
    if (!e || n <= 0) return false;
    e.foeAnima -= n;
    e.aggro = Math.max(0, (e.foe.anima || 1) - Math.max(0, e.foeAnima));
    foeVitals.current.anima = Math.max(0, e.foeAnima);
    foeVitals.current.hitAt = now();
    shake.current = Math.max(shake.current, 0.62);
    if (e.foeAnima <= 0) {
      e.broken = true;
      say("The second ring is the one that opens it.");
      endEncounter(true);
      return true;
    }
    return false;
  }, [say, endEncounter]);

  // ══════════════════════════════════════════════════════════
  //  A · THE WIND MUST BE ABLE TO END — fixed 2026-07-30
  // ══════════════════════════════════════════════════════════
  // THE BUG. Every MISS did `v.lyf -= 1` and, finding Lyf at or
  // below zero, set `windedUntil = t + exhaustMs`. But a winded
  // beb cannot press anything, so every strike during a wind
  // auto-MISSes — and re-armed the full timer. Beat gap is
  // 700–1250ms; exhaustMs is 1200–3000ms. The clock was always
  // pushed forward faster than it could run out.
  //
  // So `tickWind` could never fire inside a fight. ONE bad answer
  // meant eating every remaining strike in the encounter. That was
  // not a death spiral, it was a coffin, and it is most of why
  // caP'n reported "far too much dying" on 2026-07-30.
  //
  // It also meant the SHARPENED ANSWER TRACK HAS NEVER DONE
  // ANYTHING. 3000ms and 1200ms are both longer than the gap that
  // re-armed them, so the ladder a player spent real ⋔ on could
  // not change a single outcome. Fixing this is what turns it back
  // into a purchase worth making.
  //
  // ONE COPY OF THE ARITHMETIC. This used to live inline in two
  // places (the Calamity path and the ordinary MISS path), which
  // is exactly the shape caP'n's own law warns about: two
  // hand-maintained copies of the same mathematics will drift.
  // Both call this now. Returns true if THIS answer is the one
  // that broke you, so the caller can speak the line once.
  const windDown = useCallback((t) => {
    const v = vitals.current;
    if (v.windedUntil && t < v.windedUntil) return false; // already out — never re-arm
    v.lyf -= 1;
    if (v.lyf > 0) return false;
    v.lyf = 0;
    v.windedUntil = t + exhaustMs;
    stats.current.exhausted += 1;
    return true;
  }, [exhaustMs]);

  // ── The foe's answer to a miss ─────────────────────────────
  const takeHit = useCallback((amount) => {
    const v = vitals.current;
    const e = encRef.current;
    shake.current = Math.max(shake.current, 0.9);
    // ── The Wizard's Cap — the Empire's handshake, once a run ──
    if (hasCap && !v.capUsed) {
      v.capUsed = true;
      say("The Wizard's Cap tips itself over your eyes. The blow lands on felt, not flesh.");
      return false;
    }
    // Counted here and not at the parry, because this is the line
    // a blow has to reach to have happened: the Wizard's Cap
    // returns above without ever touching Anima, and a blow that
    // landed on felt did not land on you.
    stats.current.struck += 1;
    stats.current.struckEnc += 1;
    // ══════════════════════════════════════════════════════════
    //  B · A WINDED BEB IS HIT FLAT — caP'n, 2026-07-30
    // ══════════════════════════════════════════════════════════
    // While the wind is on you, you cannot parry at all — the
    // input handler refuses every press. So the fen is not beating
    // an opponent, it is beating a thing that cannot move, and
    // charging T'yphiz's full Lyt 3 for each of those is charging
    // you for a fight you were not allowed to be in.
    //
    // Flat 1, and it stacks up fast enough to still be frightening
    // — with the wind ending properly again (see `windDown`) a
    // maxed Sharpened Answer eats roughly one free hit where a
    // fresh beb eats three. That gap IS the track.
    //
    // Deliberately NOT zero. Being winded has to cost Anima or the
    // bar means nothing, and Anima was always the only fail state.
    const dmg = now() < v.windedUntil
      ? RULES.WINDED_HIT
      : (amount || (e?.foe?.lyt || 1));
    // R-54 — the only line in the title every blow that reaches
    // Perry has to cross. The Wizard's Cap returns above this, and
    // correctly so: a blow that landed on felt did not land on you.
    stats.current.pureUntouched = false;
    v.anima -= dmg;
    if (v.anima <= 0) {
      // ══════════════════════════════════════════════════════
      //  THE TEACHING ROAD DOES NOT KEEP YOU — caP'n, 2026-07-30
      // ══════════════════════════════════════════════════════
      // The line the banner has been promising and nothing has
      // been enforcing. `takeHit` reached zero and called
      // `fall()` on the Cinders Road exactly as it does in the
      // fen; YOU CANNOT FALL HERE was rendered in gold over a
      // road that could and did.
      //
      // Anima comes ALL the way back, not to one. A trickle would
      // turn the teaching road into a slow grind at the bottom of
      // the bar, and nothing about being spared should feel worse
      // than being alive.
      //
      // IT SITS ABOVE RAYS OF HOPE ON PURPOSE. The Blood Bargain
      // is a Tome you paid blood for, once per Solrend, and
      // spending it on a road that was never going to kill you is
      // the single worst trade in the title. The road takes the
      // hit; your Tome keeps its charge for a road that means it.
      if (teachingRef.current) {
        v.anima = animaCap;
        stats.current.spared += 1;
        stats.current.sparedEnc += 1;
        shake.current = Math.max(shake.current, 1.2);
        say(TUTORIAL.sparedLine);
        syncHud();
        return false;
      }
      // ── Rays of Hope — the Blood Bargain pays out ──
      if (v.raysArmed) {
        v.raysArmed = false;
        v.anima = 1;
        fire("consumeTome", "tome_rays_of_hope");
        say("The blood you paid at Whyllie's counter comes due — in your favor. You stand at one.");
        return false;
      }
      v.anima = 0;
      syncHud();
      fall();
      return true;
    }
    return false;
  }, [hasCap, say, fire, syncHud, fall, animaCap]);

  // ── GRADING — the one place a parry is judged ──────────────
  const resolve = useCallback((g0, strike) => {
    const v = vitals.current;
    const e = encRef.current;
    const t = now();
    let g = g0;

    // ══════════════════════════════════════════════════════════
    //  THE REGALIA SPEAKS FIRST
    // ══════════════════════════════════════════════════════════
    // Before the Ladle, before the chain, before anything at all
    // looks at this grade. The Answer, the Emperium, Shadow and
    // Mirage each get one chance to overrule what just happened,
    // in that order and for the reasons written in perryPowers.
    //
    // It runs on EVERY path — including a Calamity beat, which is
    // deliberate and is most of why you would carry an Emperium
    // into the fens at all.
    const cv = convertGrade(powers, g, t);
    if (cv.say) say(cv.say);

    // ── INTANGIBLE — you were not there ──
    // Not a parry and not a miss. Nothing is answered, so nothing
    // is earned: no Renown, no ⋔, no chain growth. But nothing is
    // lost either — the chain FREEZES rather than breaking, the
    // ATV flame keeps burning, and no Anima moves. It is the
    // cleanest "this beat did not happen" the engine has.
    //
    // A Calamity's queue survives it too, for the same reason: the
    // blow passed through, so the combo was never interrupted.
    if (cv.intangible) {
      setGrade({ g: "HELD", at: t, angle: strike?.angle || null, atk: strike?.id || null });
      guardRef.current = { key: strike?.angle || guardRef.current.key, at: t, grade: "HELD" };
      foeVitals.current.ringAt = t;
      setFoePhase("recover", {}, e?.actor ?? 0);
      scheduleNext(t, strike);
      syncHud();
      return;
    }
    g = cv.g;
    // Mirage owes a bite. It is paid AFTER the grade is settled,
    // through the one place a foe loses Anima, and it can end the
    // encounter right here — which is a legal and very satisfying
    // way for a fight to stop.
    if (cv.bite && freeBite(cv.bite)) return;

    // The Pyre Ladle, doing whatever it is that it does.
    if (g === "GOOD" && e && e.ladleLeft > 0) {
      e.ladleLeft -= 1;
      g = "PERFECT";
      say("The ladle does something. You do not see what. It was perfect.");
    }

    setGrade({ g, at: t, angle: strike?.angle || null, atk: strike?.id || null });
    guardRef.current = { key: strike?.angle || guardRef.current.key, at: t, grade: g };
    sparkRef.current = {
      at: t, angle: strike?.angle || "D", grade: g,
      tell: strike?.tell || "#ffb058",
    };
    // the bar reacts to EVERY answer, and dims only on a real bite
    foeVitals.current.ringAt = t;
    foeVitals.current.grade = g;

    if (g === "PERFECT" || g === "GOOD") {
      stats.current.parries += 1;
      bumpChain();
      purse(g === "PERFECT" ? RULES.KINDLING_PERFECT : RULES.KINDLING_GOOD);

      // ── THE PERFECT STREAK — caP'n, 2026-07-29 ──
      // "Successful PERFECT back-to-back parries starting at 3 give
      //  you a little extra currency."
      //
      // A GOOD does not break your CHAIN — you did answer it — but
      // it absolutely breaks your STREAK, because the streak is a
      // claim about precision and a GOOD is a claim about survival.
      // Two ledgers, two virtues, paid separately.
      if (g === "PERFECT") {
        stats.current.streak += 1;
        if (stats.current.streak > stats.current.bestStreak) {
          stats.current.bestStreak = stats.current.streak;
        }
        if (stats.current.streak >= RULES.STREAK_AT) {
          purse(RULES.STREAK_BONUS);
          if (stats.current.streak === RULES.STREAK_AT) {
            say("Three clean in a row. Something in the fire starts paying attention to you.");
          }
        }
      } else {
        stats.current.streak = 0;
      }
      fire("parry", g);

      // ── THE REVEAL — once per Soul, on the first landed answer ──
      // Gated on a milestone, not on a ref: a ref would replay it
      // every time the player restarts the road, and the whole
      // point of a reveal is that it is a thing that happened. App
      // grants the milestone grant-before-flag, like everything.
      //
      // It waits for a LANDED parry deliberately. caP'n wrote it as
      // "after Perry's first Parry" — a miss is not a parry, and a
      // scabbard congratulating a beb who just got hit is a joke
      // landing on the wrong beat.
      if (!soul?.milestones?.sheathe && !revealed.current) {
        revealed.current = true;
        tellBeats(sheatheSays("reveal"));
        fire("sheathe");
      }

      // ── ECHO — the answer says itself again ──
      // Deliberately AFTER the grade is banked and deliberately
      // NOT inside convertGrade: conversion decides what grade
      // happened, Echo decides what a grade that already happened
      // is worth. Fold them together and a Mirage-converted miss
      // pays out two free hits for one breath.
      const rung = spendEcho(powers, g);
      if (rung) {
        say("Your answer arrives, and then arrives again. The second one does not care what it is wearing.");
        if (freeBite(rung)) return;
      }
    }

    // ── CALAMITY — all three beats or none of it ──
    if (strike?.cal) {
      if (g === "MISS") {
        if (e) e.calQueue = null;
        say("The whole of it lands at once. It was always going to be all or nothing.");
        stats.current.chain = 0;
        stats.current.streak = 0;
        v.atv = 0;
        if (windDown(t)) say("Your paws stop listening. Everything in you is spent, and the fen has noticed.");
        const dead = takeHit((e?.foe?.lyt || 1) + 1);
        if (dead) return;
        setFoePhase("recover", {}, e?.actor ?? 0);
        scheduleNext(t, strike);
        syncHud();
        return;
      }
      if (strike.cal.idx + 1 >= strike.cal.total) {
        // Survived the whole thing. THE FRACTURE.
        stats.current.calamities += 1;
        fractureRef.current = t + RULES.FRACTURE_MS;
        if (e) e.calQueue = null;
        say("You answered all of it. The fire has nowhere left to be — it comes apart, wide open.");
        setGrade({ g: "FRACTURE", at: t + 1, angle: strike.angle, atk: "calamity" });
        setFoePhase("fracture", {}, e?.actor ?? 0);
        purse(RULES.KINDLING_FRACTURE);
        if (g === "PERFECT") { stats.current.perfects += 1; if (hasATV && v.atv < 3) v.atv += 1; }
        if (e) e.nextAt = t + 400;
        syncHud();
        return;
      }
      // mid-calamity: keep the queue rolling, no bite between beats
      if (g === "PERFECT") { stats.current.perfects += 1; if (hasATV && v.atv < 3) v.atv += 1; }
      scheduleNext(t, strike);
      syncHud();
      return;
    }

    if (g === "PERFECT") {
      stats.current.perfects += 1;
      if (hasATV && v.atv < 3) v.atv += 1;         // ATV — the Kindled Blade stacks
      if (bite()) return;                          // encounter ended inside
      setFoePhase("recover", {}, e?.actor ?? 0);
    } else if (g === "GOOD") {
      shake.current = Math.max(shake.current, 0.24);
      // ── A GOOD BITES NOW (caP'n, 2026-07-29) ──
      // "Any parry chips the enemy." This used to require an open
      // Fracture window; it requires nothing. A GOOD and a PERFECT
      // take the same piece out of a foe — what a PERFECT buys on
      // top is the ATV stack, the streak, and the better pay. The
      // difference between the two grades is now about what YOU
      // gain, never about whether the foe felt it.
      if (bite()) return;
      setFoePhase("recover", {}, e?.actor ?? 0);
    } else if (g === "MISS") {
      stats.current.chain = 0;
      stats.current.streak = 0;
      v.atv = 0;
      // ── BREATH SPENT ON BEING WRONG (caP'n, 2026-07-29) ──
      // A LANDED answer costs nothing — parry cleanly forever and
      // this line never runs. Only a miss takes Lyf, and when Lyf
      // hits zero your paws stop listening for `exhaustMs`.
      //
      // You do not die of it. The fen simply gets to keep swinging
      // at something that cannot answer, and the Anima that costs
      // you is the Anima that was always the only fail state.
      if (windDown(t)) say("Your paws stop listening. Everything in you is spent, and the fen has noticed.");
      const dead = takeHit();
      if (dead) return;
      setFoePhase("recover", {}, e?.actor ?? 0);
    }

    scheduleNext(t, strike);
    syncHud();
  }, [bumpChain, fire, purse, hasATV, say, syncHud, bite, freeBite, takeHit, windDown, tellBeats, soul, setFoePhase, scheduleNext]);

  // ══════════════════════════════════════════════════════════
  //  SPEAKING A POWER — keys 1 … RULES.SLOTS, ONE GATE
  // ══════════════════════════════════════════════════════════
  // R-UNIVERSAL: this is the only way any power is ever activated,
  // by anybody, for any reason. It takes a SLOT INDEX, not an id —
  // the key is a fact about your Grimoire, not about the power,
  // and nothing downstream of here is allowed to think otherwise.
  //
  // The legality and the arithmetic both live in perryPowers. What
  // stays here is exactly the three things only the director can
  // do: refill Chao to Max (it owns the ceiling), hand charges to
  // the coals (it owns Sight), and paint the sky (it owns the act).
  const speakSlot = useCallback((index) => {
    const v = vitals.current;
    const id = slots[index];
    if (!id) return;
    const p = REGALIA[id];
    if (!p) return;

    // ── SPEAKABLE ON THE ROAD, 2026-07-29 ──────────────────────
    // The `if (!encRef.current) return;` that used to sit here is
    // gone. caP'n: "so I can cast an Impera before battle and have
    // the effect work."
    //
    // Every reason for that gate died with the per-encounter Chao
    // refill. It existed because arming Shadow between fights was
    // strictly free — breath came back at the next foe anyway — so
    // the gate stopped a player exploiting a currency that reset.
    // Chao now spans a whole leg, so pre-casting spends something
    // real, and letting a beb ready himself before he can see what
    // is coming is a decision, not an exploit.
    //
    // THE STEERING LAW applies to the downside too: you may burn
    // your Emperium on an empty stretch of road. Nothing stops you.
    // It is a stupid move, it is visibly a stupid move, and taking
    // it away from you would be the worse call.

    // ══════════════════════════════════════════════════════════
    //  THE LEVEL-3 GATE — once per KIT UP, not once per fight
    // ══════════════════════════════════════════════════════════
    // caP'n, 2026-07-29: "the big ones are only one use per refresh
    //  — Emperium, U'hlti, any level 3 Art."
    //
    // Checked HERE, at the one gate, off the Art's declared `rung`
    // rather than a hardcoded list of two ids — so the ninth level-3
    // Art we invent obeys this without anybody remembering to come
    // back and add it. (Exception Law: data, not an enum.)
    const level3 = p.rung === "uhlti" || p.rung === "emperium";
    if (level3 && v.spentL3.has(id)) {
      say(`${p.name} is spent. It comes back at the next fire, and not before.`);
      return;
    }

    const res = speak(powers, id, v, {
      now: now(),
      theme,
      animaCap,
      hourMult: hourMult(hourLit.current),
    });

    if (!res.ok) { if (res.say) say(res.say); return; }
    // R-54 — SPEAKING an Art is what breaks the refusal, not owning
    // one. Owning is a fact about your sword; speaking is a
    // decision you made in a fight. Set after the legality gate so
    // a refused Art (no Chao, already spent this fire) costs you
    // nothing you did not actually do.
    stats.current.pureNoArts = false;
    if (level3) v.spentL3.add(id);

    // THE GROWTH LAW's ledger. Only breath you ACTUALLY spent
    // counts against your restraint — a Hollow Crown activation
    // leaves the ceiling-raise intact, which is most of what makes
    // that Tome worth its price.
    //
    // TWO COUNTERS NOW. `…ThisEnc` survives for anything that still
    // asks about one fight; `…ThisLeg` is what the Growth Law reads
    // at the next fire, and it is the harder question.
    if (res.chaoSpent) {
      stats.current.chaoSpentThisEnc += res.chaoSpent;
      stats.current.chaoSpentThisLeg += res.chaoSpent;
    }
    if (res.refillChao) v.chao = maxChao;
    if (res.sight) v.sight += res.sight;
    if (res.emperium) {
      // THE LANDSCAPE. One string, read by machinery that already
      // exists — see biomeRef's comment. No new renderer, no new
      // file, and the real thing can be built on top of it later
      // without this line changing.
      biomeRef.current = powers.emp.theme?.biome || "emperium";
      shake.current = Math.max(shake.current, 1);
    }
    if (res.say) say(res.say);
    syncSight();
    syncHud();
  }, [slots, theme, animaCap, maxChao, say, syncHud, syncSight]);

  // KEPT so nothing that called the old name breaks. It resolves an
  // id to the slot you actually put it in — because the id is the
  // stable thing (vyren pattern) and the key never was.
  const activateImpera = useCallback((imperaId) => {
    const i = slots.indexOf(imperaId);
    if (i >= 0) speakSlot(i);
  }, [slots, speakSlot]);

  // ── LIVE REF — the loop calls THROUGH this, never a closure ──
  const live = useRef({});
  live.current = {
    startEncounter, endEncounter, resolve, say, cutTo, fire, syncHud, fall,
    setFoePhase, scheduleNext, takeHit, purse, syncSight,
    // ── 2026-07-29 ── the wind clock and the fire on the road.
    // ANYTHING `step` CALLS MUST BE LISTED HERE. The rAF loop
    // reaches the director exclusively through this object, so a
    // verb missing from this list is not a lint warning — it is a
    // TypeError sixty times a second. (It was, on 2026-07-29.)
    //
    // AND IT WAS AGAIN ON 2026-07-30, with `tickSpeech` — the note
    // above was already here and did not stop it, so here is the
    // part the note was missing: THE CHECK HAS TO READ THE CALL
    // SITE, NOT THE DECLARATION. Verifying `tickSpeech` exists as
    // a useCallback proves nothing at all; the loop never looks
    // there. The only audit that catches this greps `step` for
    // `live.current.X(` and asserts every X appears in this object.
    tickWind, tickSpeech, kitUp,
    // ── 2026-07-30 ── the report card. Called by `step` the frame
    // after a fight's hold runs out, so it lives here or it is a
    // TypeError sixty times a second. (See the note above: the
    // audit that catches this greps `step` for `live.current.X(`
    // and asserts every X appears in this object. It was run.)
    raiseCard,
  };

  // ── THE LOOP — mounts once, runs the whole act ─────────────
  useEffect(() => {
    running.current = true;
    last.current = now();
    beat.current = 0;
    beatT.current = 0;
    flags.current = {};

    const advance = () => {
      beat.current += 1;
      beatT.current = 0;
      const nb = script[beat.current];
      setBeatIndex(beat.current);
      if (!nb) return;
      if (nb.shot) live.current.cutTo(nb.shot);
      if (nb.line) live.current.say(nb.line);
      else if (nb.type === "title") live.current.say(null);
    };

    // Build and arm a strike. Everything the stage needs to
    // PERFORM it is written into the actor's foeState.
    const launch = (e, attackId, angle, t, opts = {}) => {
      const v = vitals.current;
      // ── KING'S GRACE and the EMPERIUM bend the same clock ──
      // Multiplicative with Still the Flame, and the order is
      // irrelevant because multiplication does not care — which is
      // exactly why the slow is a MULTIPLIER and not an addition.
      // Two powers slowing the world are two clocks being bent,
      // not one clock bent twice.
      const wm = windMultiplier(powers, t);
      const scale = opts.scale != null
        ? opts.scale * wm
        : windScale(e.foe.tempo || 1, e.aggro, v.slowLeft > 0) * wm;
      const st = makeStrike(attackId, angle, t, { ...opts, scale });
      if (v.foresight > 0 && !opts.echo) { st.foreseen = true; v.foresight -= 1; }
      if (v.slowLeft > 0 && !opts.echo) v.slowLeft -= 1;
      // ── SPEND A CHARGE ──
      // One charge names the WHOLE rose for the length of one
      // strike, not just the coal that lights. Naming five coals
      // you did not need is what tells you the sixth is the one
      // coming — a single lit letter would give away the answer
      // outright, which is a different (and much worse) game.
      //
      // Never spent on an echo (a Slash's second beat is the same
      // motion as its first) and never spent while the player has
      // switched hints off — no burning a Tome on someone who
      // asked not to see it.
      if (!opts.echo && v.sight > 0 && hintMode !== false) {
        v.sight -= 1;
        st.named = true;
      }
      live.current.syncSight();
      strikeRef.current = st;
      e.lastAngle = angle;
      const actor = e.actor;
      const s0 = foeState.current[actor];
      if (s0 && s0.phase !== "hidden") {
        // ── THE APPROACH INTENT, 2026-07-27 ──
        // The director declares WHICH LANE and WHICH PATH. It does
        // NOT compute where that is in the world, because "left"
        // is a fact about the camera and the camera lives in the
        // stage. Writing a world X here is how the rose collapsed
        // onto the road axis in the first place.
        foeState.current[actor] = {
          phase: st.feint ? "feint" : (attackId === "hurl" ? "cast" : "wind"),
          t: 0, prog: 0, angle, atk: attackId,
          lane: (GUARD.DIRS[angle] || GUARD.DIRS.D).lane,
          path: pathOf(st),
          // THE EXCEPTION LAW, on the wire. The body no longer asks
          // "which attack is this" and hardcodes a pose per id — it
          // asks what this strike wants its body to DO, and any new
          // attack that says "kick" kicks for free.
          body: st.body || "swing",
          fr: st.fr || null,
          actor: true,
        };
      }
      // every OTHER living foe stands down and gives the lane room
      for (let i = 0; i < 2; i++) {
        if (i === actor) continue;
        const so = foeState.current[i];
        if (so && so.phase !== "hidden") {
          foeState.current[i] = { ...so, actor: false, lane: -((GUARD.DIRS[angle] || GUARD.DIRS.D).lane) || 1 };
        }
      }
      if (attackId === "hurl") {
        // TIMING ONLY. The orb's geometry is solved on the camera
        // plane by the stage, same as everything else that has to
        // land where a key says it will.
        orbRef.current = {
          active: false, t0: st.travelAt, t1: st.impactAt,
          tell: st.tell, angle,
        };
      }
      return st;
    };

    const step = () => {
      raf.current = requestAnimationFrame(step);
      const t = now();
      const dt = Math.min(0.05, (t - last.current) / 1000);
      last.current = t;
      // ── THE PAUSE ──
      // Above `running`, below the clock. The rAF keeps running so
      // the stage still draws and the camera still breathes; the
      // ROAD stops. Every absolute deadline that was live when we
      // stopped is moved forward by exactly this long when we
      // start again — see `resumeFromCard`, which is the half of
      // this that is easy to forget and expensive to.
      if (paused.current) { speed.current = 0; return; }
      if (!running.current) { speed.current = 0; return; }

      shake.current *= Math.max(0, 1 - dt * 4.2);
      live.current.tickWind(t);
      live.current.tickSpeech(t);

      // ── THE REGALIA'S CLOCKS ──
      // Every frame, unconditionally, INCLUDING while the fight is
      // held between beats. A King's Grace that paused because
      // nothing was swinging at you would be a King's Grace you
      // could bank, and the whole point of an hour is that it
      // spends itself whether or not you use it.
      const closed = tickPowers(powers, t);
      if (closed) {
        biomeRef.current = powers.emp.until ? biomeRef.current : null;
        live.current.say(closed);
        live.current.syncHud();
      }

      // the Treasure relaxes out of its guard on its own
      const gd = guardRef.current;
      if (gd.key && t - gd.at > QTE.GUARD_HOLD_MS) guardRef.current = { key: null, at: 0, grade: null };

      if (t < holdUntil.current) { speed.current = 0; return; }

      // ── THE REPORT CARD RISES ──
      // The break has had its second and a half; now the bill.
      // Deliberately before the beat script advances, so the card
      // is up while the road is still standing where the fight
      // was, not halfway to the next one.
      if (cardPending.current) { live.current.raiseCard(); return; }

      const b = script[beat.current];
      if (!b) return;
      beatT.current += dt * 1000;
      const F = flags.current;

      // ── WALK ──
      if (b.type === "walk") {
        if (pos.current.x < b.to) {
          speed.current = 1;
          pos.current.x = Math.min(b.to, pos.current.x + RULES.WALK_SPEED * dt);
          pos.current.z = Math.sin(pos.current.x * 0.22) * 0.14;   // the road wanders
        } else {
          speed.current = 0;
          advance();
        }
        return;
      }

      // ── KIT UP — a fire on the road ────────────────────────
      // caP'n's checkpoints, as a beat type. `kind` picks which
      // one it is (bonfire · chapel · lady · stranger · statue ·
      // village) and the KITUPS table supplies its line, so a
      // seventh kind of fire is a data row and nothing else.
      //
      // The walk-onto-the-mark shape is copied from `qte` on
      // purpose: you should always arrive at a fire on your own
      // feet, never be teleported to one.
      if (b.type === "kit") {
        if (pos.current.x < (b.at ?? pos.current.x) - 0.02) {
          speed.current = 1;
          pos.current.x = Math.min(b.at, pos.current.x + RULES.WALK_SPEED * dt);
          pos.current.z = Math.sin(pos.current.x * 0.22) * 0.14;
          return;
        }
        speed.current = 0;
        if (!F[`kit${beat.current}`]) {
          F[`kit${beat.current}`] = true;
          live.current.kitUp(b.kind || "bonfire");
        }
        if (beatT.current >= (b.dur || 2600)) advance();
        return;
      }

      // ── TITLE / LOOK ──
      if (b.type === "title" || b.type === "look") {
        speed.current = 0;
        facing.current = b.type === "look" ? Math.PI : 0;
        if (beatT.current >= (b.dur || 3000)) {
          facing.current = 0;
          advance();
        }
        return;
      }

      // ── ARRIVE ──
      if (b.type === "arrive") {
        speed.current = 0;
        if (!F.arrived) {
          F.arrived = true;
          running.current = false;
          stats.current.arrived = true;
          // ── ⋔ THE TUTORIAL'S COMPLETION PURSE, 2026-07-30 ──
          // Banked here and settled with the rest of the run's ⋔
          // in one transaction, like everything else this road
          // pays. The ESC door never reaches this beat, which is
          // the entire mechanism: walking is worth exactly this
          // much more than leaving, and no other line anywhere
          // has to know a skip is possible.
          if (teachingRef.current) live.current.purse(RULES.KINDLING_TUTORIAL);
          setOver("arrived");
        }
        return;
      }

      // ── THE FIGHT ──
      if (b.type !== "qte") return;
      const key = `q${beat.current}`;
      const e = encRef.current;

      if (!e) {
        if (!F[key]) {
          // walk onto the mark, then the fire moves
          if (pos.current.x < b.at - 0.02) {
            speed.current = 1;
            pos.current.x = Math.min(b.at, pos.current.x + RULES.WALK_SPEED * dt);
            pos.current.z = Math.sin(pos.current.x * 0.22) * 0.14;
            return;
          }
          speed.current = 0;
          F[key] = true;
          live.current.startEncounter(b.enc);
          return;
        }
        advance();          // encounter finished (broken or retreated) → move on
        return;
      }

      // ── FEET ARE THE STAGE'S JOB NOW ──
      // A foe walks to the lane its next blow comes from, and a
      // lane is a screen fact. `LaneDriver` in CinematicStage owns
      // every foe's position during an encounter; the director
      // only says which lane and when. (Was: a hardcoded
      // pos.x + 1.15 shamble that parked every enemy on the road
      // axis forever, which is why all six angles looked alike.)

      const st = strikeRef.current;

      // ── SPAWN — roll the next strike out of the moveset ──
      if (!st && t >= e.nextAt && (e.beatsLeft > 0 || e.calQueue || e.echo)) {
        // a Slash's second beat, already owed
        if (e.echo) {
          const { angle, scale } = e.echo;
          e.echo = null;
          launch(e, "slash", angle, t, { echo: true, scale });
          return;
        }
        // the next beat of a Calamity in progress
        if (e.calQueue && e.calQueue.length) {
          const nxt = e.calQueue.shift();
          launch(e, "calamity", nxt.angle, t, {
            cal: { idx: nxt.idx, total: nxt.total },
            scale: nxt.scale,
          });
          return;
        }

        e.beatsLeft -= 1;
        if (e.count > 1) e.actor = R() < 0.5 ? 0 : 1;
        const atkId = rollAttack(R, e.moveset, e.lastAtk, e.aggro, e.repeats);
        e.repeats = atkId === e.lastAtk ? e.repeats + 1 : 0;
        e.lastAtk = atkId;
        const angle = rollAngle(R, atkId, e.lastAngle);

        if (atkId === "calamity") {
          const A = ATTACKS.calamity;
          const scale = windScale(e.foe.tempo || 1, e.aggro, vitals.current.slowLeft > 0);
          const total = A.beats || 3;
          const queue = [];
          let la = angle;
          for (let i = 1; i < total; i++) {
            la = rollAngle(R, "calamity", la);
            queue.push({ angle: la, idx: i, total, scale: (A.beatGap / A.wind) });
          }
          e.calQueue = queue;
          live.current.say("It stops fighting you and starts opening. Three answers, or all of it at once.");
          launch(e, "calamity", angle, t, { cal: { idx: 0, total }, scale });
          return;
        }

        launch(e, atkId, angle, t);
        return;
      }

      // ── PERFORM — the live strike advances ──
      if (st) {
        const span = st.impactAt - st.spawnAt;
        const prog = span > 0 ? (t - st.spawnAt) / span : 1;
        const actor = e.actor;
        const fs = foeState.current[actor];
        if (fs && fs.phase !== "hidden") fs.prog = prog;

        // the orb leaves the hand — timing only, geometry on the stage.
        // GUARDED ON `hurl`: the Chain Kunai also carries a travelAt
        // (its haul), and it is drawn by its own stage layer. Without
        // this the boss would fire a phantom ember every throw.
        if (st.id === "hurl" && st.travelAt && !orbRef.current.active && t >= st.travelAt) {
          orbRef.current = {
            active: true, t0: st.travelAt, t1: st.impactAt,
            tell: st.tell, angle: st.angle,
          };
          if (fs && fs.phase !== "hidden") { fs.phase = "hurl"; fs.actor = true; }
        }

        // the arm comes down just before impact
        if (!st.feint && !st.travelAt && t >= st.impactAt - 150 && fs && fs.phase === "wind") {
          fs.phase = "strike";
          fs.t = 0;
        }

        if (isTimedOut(st, t)) {
          strikeRef.current = null;
          orbRef.current.active = false;
          if (st.feint) {
            // holding still through a lie is the correct answer
            setGrade({ g: "HELD", at: t, angle: st.angle, atk: "feint" });
            // ── ATV: THE LONG HOUR GOES OUT ──
            // The foe ended it by refusing its requirement, and
            // refusing destroys it. That is what ATV means, and it
            // is why the Hour is 55 ⋔ instead of 15: a lie is worth
            // more against a beb holding a clock than against one
            // who is not.
            if (hourLit.current) {
              hourLit.current = false;
              live.current.say("It withheld. So did you. Good — but the hour goes out with the lie.");
            } else {
              live.current.say("It withheld. So did you. Good.");
            }
            live.current.purse(RULES.KINDLING_HELD);
            live.current.setFoePhase("recover", {}, e.actor);
            live.current.scheduleNext(t);
          } else {
            // a Slash still owes its second beat even if you ate the first
            if (st.id === "slash" && !st.echo && !treasure.twin) {
              e.echo = {
                angle: mirrorAngle(st.angle),
                scale: Math.max(0.55, (ATTACKS.slash.double || 520) / ATTACKS.slash.wind),
              };
            }
            live.current.resolve("MISS", st);
          }
        }
        return;
      }

      // ── the fight is over one way or the other ──
      if (e.beatsLeft <= 0 && !e.calQueue?.length && !e.echo && t >= e.nextAt) {
        live.current.say("It has nothing left and shambles off the road. You never broke it — so it owes you nothing.");
        live.current.endEncounter(false);
      }
    };

    live.current.cutTo(script[0].shot);
    live.current.say(script[0].line || null);
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── KEYBOARD — the only input in the game ──────────────────
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.repeat) return;                       // THE GUARD. Every discrete key.

      // ══════════════════════════════════════════════════════════
      //  THE REPORT CARD EATS EVERY KEY — 2026-07-30
      // ══════════════════════════════════════════════════════════
      // First, above everything, because while the card is up the
      // road is not running and a guard press has nothing to be
      // pressed at. Any key dismisses it — the card asks for
      // acknowledgement, not for a decision, and making a player
      // hunt for the right key to close a scoreboard is the
      // cheapest way to make them stop reading it.
      //
      // CARD_LOCK_MS is why the key you were mid-parry with does
      // not dismiss the thing before your eyes have reached it.
      if (cardRef.current) {
        ev.preventDefault();
        if (now() < cardRef.current.lockUntil) return;
        resumeFromCard();
        return;
      }

      // ══════════════════════════════════════════════════════════
      //  ESC — THE DOOR OUT OF THE TEACHING ROAD
      // ══════════════════════════════════════════════════════════
      // The affordance the banner has been advertising since the
      // Tutorial shipped, attached to nothing. ESC was wired in
      // Customize, the Codex, Options, Species and MainMenu — in
      // every screen in this title except the one that told you to
      // press it.
      //
      // It only exists where the act SAYS it exists: `skippable`
      // is a separate field from `teaches` on purpose, so a later
      // level can teach without offering a way out, and a level
      // can offer a way out without teaching. Neither of those
      // costs a line of code — they cost a word in a data row.
      if (ev.key === "Escape") {
        if (!teachingRef.current || !act.skippable || over) return;
        ev.preventDefault();
        if (skipArm.current && now() < skipArm.current) { skipRoad(); return; }
        armSkip();
        return;
      }

      // ── THE GRIMOIRE'S KEYS ──
      // Derived from RULES.SLOTS, never written out. A sixth slot
      // is a number in perryData and this handler already knows
      // about it — which is the whole reason the keys moved off the
      // powers and onto the rack.
      const si = SLOT_KEYS.indexOf(ev.key);
      if (si >= 0) { ev.preventDefault(); speakSlot(si); return; }
      const k = ev.key.toUpperCase();
      if (!GUARD.KEYS.includes(k)) return;
      ev.preventDefault();

      const t = now();

      // ══════════════════════════════════════════════════════════
      //  WINDED — your paws are somebody else's for a moment
      // ══════════════════════════════════════════════════════════
      // The most important line added on 2026-07-29, and the reason
      // it is HERE rather than inside gradePress: a winded beb does
      // not press badly, he does not press AT ALL. The Treasure does
      // not pose, the guard does not register, the strike stays live
      // and lands on him.
      //
      // Nothing about this punishes you twice — the miss already
      // took the Lyf. This is just the shape of having none left.
      if (t < vitals.current.windedUntil) return;

      const st = strikeRef.current;

      // The Treasure moves whether or not anything is coming. A
      // guard thrown at nothing is still a guard thrown — it reads,
      // it costs nothing, and it is how you learn the rose.
      guardRef.current = { key: k, at: t, grade: null };

      if (!st) return;
      const g = gradePress(st, k, t, mods);
      if (g === "EARLY" || g === "PROBE") return;  // ignored, never punished

      st.answered = true;
      strikeRef.current = null;
      orbRef.current.active = false;

      const e = encRef.current;
      // a Slash owes a second beat, mirrored — unless you are
      // holding the Twin Ember Needles, which answer both at once.
      if (e && st.id === "slash" && !st.echo && !treasure.twin && g !== "MISS") {
        e.echo = {
          angle: mirrorAngle(st.angle),
          scale: Math.max(0.55, (ATTACKS.slash.double || 520) / ATTACKS.slash.wind),
        };
      }
      live.current.resolve(g, st);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [speakSlot, mods, treasure, resumeFromCard, skipRoad, armSkip, act.skippable, over]);

  // ── Hand the finished run upward, once ─────────────────────
  useEffect(() => {
    if (over && onFinish) onFinish(over, { ...stats.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over]);

  return {
    // refs — the 3D stage reads these inside useFrame, never props
    pos, facing, speed, shake, foePos, foeState, foeId, shotRef, fpRef,
    strikeRef, guardRef, orbRef, sparkRef, fractureRef, foeVitals, sightRef,
    // discrete state — the DOM overlay renders these
    grade, hud, line, over, beatIndex,
    beat: script[beatIndex] || script[0],
    animaCap, maxChao, imperas, treasure, finish,
    // ── THE GRIMOIRE ──
    // The overlay draws the rack off this; the keys are the index
    // plus one, computed by slotKey and nowhere else.
    slots, theme,
    // ── THE ACT, and the Emperium's sky ──
    // `CinematicStage` reads `director.act?.biome` and has since
    // the Drowned Mile pass. It has been reading `undefined` this
    // whole time and falling back to "cinders", which is correct
    // for the Cinders Road and is why nobody noticed. Now the
    // Emperium can answer that question for twenty seconds at a
    // time, through machinery that already existed.
    //
    // NOTE, and it is a real one: the ACT itself still does not
    // supply a biome here — Act II's director wiring is a separate
    // job and is logged in the Roadmap. This returns the override
    // only. When the act wiring lands, the fallback below is the
    // line it changes, and it is one line.
    // An Emperium still overrides the dressing for its duration
    // (biomeRef); underneath it, the ACT decides — which is the
    // line that was hardcoded and is the reason the fen was dark.
    act: { id: act.id, biome: biomeRef.current || act.biome || "cinders", skippable: !!act.skippable },
    // `tutorial` is DERIVED from the act row AND from the Soul,
    // never stored. It used to read `!!act.teaches` alone, which
    // meant the banner promised deathlessness on a fifth walk
    // down a road that had stopped lettering its coals two walks
    // earlier. One predicate answers it now — see `isTeaching`.
    tutorial: teaching,
    // ── THE TUTORIAL'S TWO SURFACES, 2026-07-30 ──
    // `card` is the report card between fights; `skipAsk` is the
    // door, half-open, waiting for the second ESC. Both are plain
    // discrete state and the overlay renders them directly.
    card, skipAsk,
    biomeRef, powers, kitRef,
    // ── 2026-07-29 ── the loadout numbers the overlay prints
    lyfCap, exhaustMs,
  };
}
